import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon, MessageSquareIcon, PhoneIcon } from 'lucide-react';
import { LoanShell } from '../components/loan/LoanShell';
import { LoanTimeline } from '../components/loan/LoanTimeline';
import { TaskList } from '../components/loan/TaskList';
import { usePortal } from '../contexts/PortalContext';
import { loanOptions } from '../data/loanOptions';
import { activity, milestones, currentMilestoneIndex } from '../data/loanStatus';
import { loanOfficer, processor } from '../data/team';
import { formatCurrency } from '../utils/format';
import { getPropertyContext } from '../utils/propertyContext';
import { useTokens } from '../hooks/useTokens';
import { PricingStatusBadge, PricingStatusNote } from '../components/loan/PricingStatusNote';
const ease = [0.23, 1, 0.32, 1] as const;
export function Dashboard() {
  const {
    tasks,
    answers,
    selectedOption,
    documents
  } = usePortal();
  const option = loanOptions.find(o => o.id === selectedOption) ?? loanOptions[0];
  const open = tasks.filter(t => !t.done).length;
  const docsLeft = documents.filter(d => d.status === 'needed' || d.status === 'attention').length;
  const property = getPropertyContext(answers);
  const {
    fill
  } = useTokens();
  return <LoanShell data-id="element-849" data-mp="2fppmcnsarap5">
      <main className="mx-auto max-w-6xl px-5 pt-8 md:px-8 md:pt-12" data-id="element-850" data-mp="2fo4wvckuahy5">
        <motion.div initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        ease
      }} data-id="element-851" data-mp="1izp12yr4zccl">
          <p className="text-ink-500" data-id="element-852" data-mp="1nz1pvp1kitht">Hi {answers.firstName}</p>
          <h1 className="mt-1 max-w-2xl text-[30px] font-semibold leading-[1.1] tracking-[-0.03em] text-ink-900 md:text-[44px]" data-id="element-853" data-mp="1s03qa9781bld">
            Your loan is in {milestones[currentMilestoneIndex].label.toLowerCase()}.{' '}
            <span className="text-ink-400" data-id="element-854" data-mp="109h46ye15i4u">{open ? `${open} thing${open > 1 ? 's' : ''} will keep it moving.` : 'Nothing needed from you.'}</span>
          </h1>
        </motion.div>

        <div className="mt-10 grid gap-10 lg:grid-cols-[1fr_340px] lg:gap-12" data-id="element-855" data-mp="27kwxjzvxpbpl">
          <div className="min-w-0 space-y-12" data-id="element-856" data-mp="22m3l8asdvqr0">
            <section aria-labelledby="needs-title" data-id="element-857" data-mp="22l26nwc5sm4j">
              <div className="mb-4 flex items-baseline justify-between" data-id="element-858" data-mp="2hb01uw8iac38">
                <h2 id="needs-title" className="text-lg font-semibold tracking-tight text-ink-900" data-id="element-859" data-mp="2ha6vhwdglolm">
                  What we need from you
                </h2>
                {docsLeft > 0 && <Link to="/documents" className="text-sm font-medium text-pulse-700 hover:underline" data-id="element-860" data-mp="1omkpkhgre7vl">
                    {docsLeft} documents left
                  </Link>}
              </div>
              <TaskList tasks={tasks} data-id="element-861" data-mp="1xkwfsidpr3ni" />
            </section>

            <section aria-labelledby="progress-title" data-id="element-862" data-mp="1tkm913i3k97l">
              <h2 id="progress-title" className="mb-6 text-lg font-semibold tracking-tight text-ink-900" data-id="element-863" data-mp="3925y31l3vpn8">
                Loan progress
              </h2>
              <LoanTimeline data-id="element-864" data-mp="35012vyf0ekyh" />
            </section>

            <section aria-labelledby="activity-title" className="pb-8" data-id="element-865" data-mp="3ckd6o9k1r487">
              <div className="mb-4 flex items-baseline justify-between" data-id="element-866" data-mp="3cmg4hfrfy1nh">
                <h2 id="activity-title" className="text-lg font-semibold tracking-tight text-ink-900" data-id="element-867" data-mp="3hkrcdky6e232">
                  Recent activity
                </h2>
                <Link to="/loan/updates" className="text-sm font-medium text-pulse-700 hover:underline" data-id="element-868" data-mp="37kgzk1g1ywof">
                  See all
                </Link>
              </div>
              <ul className="space-y-4" data-id="element-869" data-mp="44a7ks702nvh">
                {activity.slice(0, 4).map(a => <li key={a.id} className="flex items-start gap-3" data-id="element-870" data-mp="464gt98wsrjq">
                    <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${a.kind === 'accepted' ? 'bg-positive-500' : a.kind === 'request' ? 'bg-caution-500' : 'bg-pulse-500'}`} aria-hidden data-id="element-871" data-mp="3lk8i8fhil18r" />
                    <div className="flex-1" data-id="element-872" data-mp="i2i0mp0x151d">
                      <p className="text-[15px] text-ink-900" data-id="element-873" data-mp="muhtjxikixog">{fill(a.title)}</p>
                      <p className="text-[13px] text-ink-400" data-id="element-874" data-mp="mwm8dai09h3i">{a.time}</p>
                    </div>
                  </li>)}
              </ul>
            </section>
          </div>

          <aside className="space-y-6 pb-12" data-id="element-875" data-mp="2qeu62hezb0jr">
            <section aria-labelledby="your-loan" className="overflow-hidden rounded-3xl bg-white ring-1 ring-ink-200" data-id="element-876" data-mp="2lcv1x5bezzlr">
              {property.photo && <img src={property.photo} alt={property.title} className="aspect-[16/9] w-full object-cover" data-id="element-877" data-mp="2ggeao28x554g" />}
              <div className="p-5" data-id="element-878" data-mp="3e4emotnntlpv">
                <h2 id="your-loan" className="text-sm text-ink-500" data-id="element-879" data-mp="392pd7ig35g3w">
                  Your loan · {property.status}
                </h2>
                <p className="mt-0.5 font-semibold text-ink-900" data-id="element-880" data-mp="34axu1sz9ecns">{property.title}</p>
                <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3 text-sm" data-id="element-881" data-mp="2zce4zjpdckr7">
                  <div data-id="element-882" data-mp="np5ea49x47m">
                    <dt className="text-ink-500" data-id="element-883" data-mp="o79tu13aoqm">Loan</dt>
                    <dd className="mt-0.5 font-medium text-ink-900" data-id="element-884" data-mp="3s2czvvq8vc3s">{option.name}</dd>
                  </div>
                  <div data-id="element-885" data-mp="45uyjfqtl4ux">
                    <dt className="text-ink-500" data-id="element-886" data-mp="46o9yu0q4ije">Rate</dt>
                    <dd className="tabular mt-0.5 font-medium text-ink-900" data-id="element-887" data-mp="86onmcg00ppe">{option.rate.toFixed(3)}%</dd>
                  </div>
                  <div data-id="element-888" data-mp="d4zxgn6an07m">
                    <dt className="text-ink-500" data-id="element-889" data-mp="d4ggp9sdqhv6">Est. payment</dt>
                    <dd className="tabular mt-0.5 font-medium text-ink-900" data-id="element-890" data-mp="i5e729njmi7r">{formatCurrency(option.monthly)}/mo</dd>
                  </div>
                  <div data-id="element-891" data-mp="n3ywrf565jhy">
                    <dt className="text-ink-500" data-id="element-892" data-mp="n2eaect0d6v6">{property.detailLabel}</dt>
                    <dd className="mt-0.5 font-medium text-ink-900" data-id="element-893" data-mp="r3zog1ha3r6y">{property.detailValue}</dd>
                  </div>
                </dl>
                <div className="mt-4 border-t border-ink-100 pt-4" data-id="element-894" data-mp="10vcd6k7s618r">
                  <PricingStatusBadge data-id="element-895" data-mp="10xq5ilb2szcm" />
                  <div className="mt-2" data-id="element-896" data-mp="15v8gsickfqns">
                    <PricingStatusNote data-id="element-897" data-mp="15w9s8nxhuf61" />
                  </div>
                </div>
              </div>
            </section>

            <section aria-labelledby="team-title" className="rounded-3xl bg-white p-5 ring-1 ring-ink-200" data-id="element-898" data-mp="1arfclib8zls0">
              <h2 id="team-title" className="text-sm text-ink-500" data-id="element-899" data-mp="g6l0qc0qf9wa">
                Your loan team
              </h2>
              <ul className="mt-4 space-y-4" data-id="element-900" data-mp="uvza3k4b5rbo">
                {[loanOfficer, processor].map(p => <li key={p.name} className="flex items-center gap-3" data-id="element-901" data-mp="q4h7krmmziol">
                    <img src={p.photo} alt="" className="h-10 w-10 rounded-full object-cover" data-id="element-902" data-mp="13w5gyn5i3t1v" />
                    <div className="min-w-0 flex-1" data-id="element-903" data-mp="yx2os3amdhbb">
                      <p className="font-medium text-ink-900" data-id="element-904" data-mp="yw9eiscgc9of">{p.name}</p>
                      <p className="text-[13px] text-ink-500" data-id="element-905" data-mp="1dulb6yztrifl">{p.title}</p>
                    </div>
                    <Link to="/loan/messages" className="grid h-9 w-9 place-items-center rounded-full text-ink-500 ring-1 ring-ink-200 transition-colors duration-150 hover:bg-ink-50" aria-label={`Message ${p.firstName}`} data-id="element-906" data-mp="18uywsm89fw57">
                      <MessageSquareIcon className="h-4 w-4" data-id="element-907" data-mp="3anwtit55r8vf" />
                    </Link>
                  </li>)}
              </ul>
              <a href={`tel:${loanOfficer.phone}`} className="mt-5 flex items-center justify-between rounded-xl bg-ink-50 px-4 py-3 text-sm font-medium text-ink-700 transition-colors duration-150 hover:bg-ink-100" data-id="element-908" data-mp="1v3nu7nowxm69">
                <span className="flex items-center gap-2" data-id="element-909" data-mp="1ldb137318koi">
                  <PhoneIcon className="h-4 w-4" aria-hidden data-id="element-910" data-mp="1lbrl7hlagbn9" /> {loanOfficer.phone}
                </span>
                <ArrowRightIcon className="h-4 w-4 text-ink-400" aria-hidden data-id="element-911" data-mp="17g5w7af21ruu" />
              </a>
            </section>
          </aside>
        </div>
      </main>
    </LoanShell>;
}